import { Flex, View } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import qs from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

// Monorepo shared components
import { commonApi, handleApiCall } from '@exchange-frontends/api';
import { Carousel, CarouselItem, CustomLinkBanner, LinkBanner } from '@exchange-frontends/components';
import { useSelectedFilters, useStaticData } from '@exchange-frontends/custom-hooks';
import { handleBrowseForLaunch } from '@exchange-frontends/utils';

import { useStore } from '../store';
import AppIntegrations from './partials/AppIntegrations';
import ArmBanner from './partials/ArmBanner';
import FeaturedSection from './partials/FeaturedSection';
import SearchResults from './partials/SearchResults';
import SelectedFilters from './partials/SelectedFilters';
import { SearchField } from './search-sidebar/SearchField';
import SideBar from './search-sidebar/SideBar';

const shouldShowSearchBanner = false;

const BrowseContainer = styled.div`
  column-gap: var(--spectrum-global-dimension-size-200, var(--spectrum-alias-size-200));
  display: ${({ $isFlex }) => ($isFlex ? 'flex' : 'block')};
`;

const cloudNames = {
  ec: 'Experience Cloud',
  cc: 'Creative Cloud',
};

/**
 * Browse page contents
 * @component
 * @category Browse
 */
const Browse = ({ cloud = 'ec' }) => {
  const { matchedBreakpoints } = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProduct = useStore((store) => store.selectedProduct);
  const productsList = useStore((store) => store.productsList);
  const product = useMemo(() => qs.parse(location.search).product ?? 'All', [location.search]);
  const hasMultipleProducts = Array.isArray(product) && product.length > 1;
  const { listingType } = qs.parse(location.search);
  const defaultListingTypes = {
    cc: 'plugins',
    ec: 'applications',
  };
  const currentListingType = listingType ?? defaultListingTypes[cloud];
  const q = qs.parse(location.search).q;
  const collectionId = qs.parse(location.search).collectionId;
  const discoverIntegrations = qs.parse(location.search).route;
  const workflow = qs.parse(location.search).workflow;

  //custom hooks
  const browseBanners = useStaticData('browseBanners');
  const categories = useStaticData('categories', cloud);

  const isEC = cloud === 'ec';
  const isCC = cloud === 'cc';

  const hostApps = useStaticData('hostApps', currentListingType);
  const selectedFilters = useSelectedFilters(cloud);
  const filterKeys = useMemo(() => {
    const defaultFilterKeys = isCC ? ['q', 'product'] : ['q'];
    return categories ? [...categories.keys(), ...defaultFilterKeys] : defaultFilterKeys;
  }, [categories, isCC]);
  const isSolution = currentListingType === 'solutions';
  const shouldHideTopBannersCarousel = useMemo(() => {
    const hasFiltersSelected = Object.keys(selectedFilters ?? {}).length > 0;
    const hasSearchQuery = Boolean(q);
    return isSolution || hasFiltersSelected || hasSearchQuery;
  }, [selectedFilters, q, isSolution]);

  const shouldHideFeaturedCarousel = useMemo(() => {
    const conditions = {
      hasSearchQuery: Boolean(q),
      isIntegration: currentListingType === 'integrations',
      hasMultipleProducts,
      isSolution,
    };

    return Object.values(conditions).some(Boolean);
  }, [q, currentListingType, isSolution, hasMultipleProducts]);

  const ccBanner = isCC
    ? browseBanners?.find((b) => b.bgImgUrl?.endsWith('adobe-creative-cloud-banner.png')).bgImgUrl
    : null;

  const isTabletSize = !matchedBreakpoints.includes('L');

  useEffect(() => {
    const parsedSearch = new URLSearchParams(location.search);
    if (isEC && parsedSearch.get('product') === 'SIGN' && parsedSearch.get('partnerLevel')) {
      parsedSearch.delete('partnerLevel');
      navigate({ search: parsedSearch.toString() }, { replace: true });
    }
  }, [isEC, location.search, navigate]);

  useEffect(() => {
    const parsedSearch = new URLSearchParams(location.search);
    // remove undefined params
    [...parsedSearch.keys()].forEach((key) => {
      if (!parsedSearch.get(key)) {
        parsedSearch.delete(key);
      }
    });
    // set default product
    if (!parsedSearch.get('product')) {
      parsedSearch.set('product', 'All');
    }
    // set default partner level
    if (!parsedSearch.get('partnerLevel') && isEC && product !== 'SIGN') {
      parsedSearch.set('partnerLevel', 'All');
    }
    // set default solutionType
    if (!parsedSearch.get('solutionType') && isSolution) {
      parsedSearch.set('solutionType', 'All');
    }
    // set default Geographies
    if (!parsedSearch.get('geographies') && isSolution) {
      parsedSearch.set('geographies', 'All');
    }
    // set default listingType based on the cloud type
    if (!parsedSearch.get('listingType')) {
      if (isEC) {
        parsedSearch.set('listingType', 'applications');
      } else if (isCC) {
        parsedSearch.set('listingType', 'plugins');
      }
    }

    // set default sort
    if (!parsedSearch.get('sort') && categories) {
      const searchParamKeys = [...parsedSearch.keys()];
      const allFilterKeys = (isCC ? [...filterKeys, 'price'] : filterKeys)?.filter((key) => key !== 'product');
      const sort =
        searchParamKeys.some((key) => allFilterKeys.includes(key)) ||
        (parsedSearch.get('product') && parsedSearch.get('product') !== 'All')
          ? 'RELEVANCE'
          : 'MOST_RECENT';
      parsedSearch.set('sort', sort);
    }
    if (`?${parsedSearch.toString()}` !== location.search) {
      navigate(
        {
          search: parsedSearch.toString(),
        },
        { replace: true }
      );
    }
  }, [location.search, isEC, isCC, navigate, filterKeys, product, categories, listingType, isSolution]);

  useEffect(() => {
    // Handle legacy cc_app_type=integrations parameter by converting to listingType=integrations
    if (searchParams.get('cc_app_type') === 'integrations') {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete('cc_app_type');
      updatedParams.set('listingType', 'integrations');
      setSearchParams(updatedParams);
    }
  }, [searchParams, setSearchParams]);

  // Remove appType=SLN from URL when listingType is applications
  useEffect(() => {
    if (listingType === 'applications' && searchParams.get('appType') === 'SLN') {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete('appType');
      setSearchParams(updatedParams);
    }
  }, [listingType, searchParams, setSearchParams]);

  useEffect(() => {
    useStore.setState({
      cloud,
    });
    return () => {
      sessionStorage.removeItem('sortByChangedByUser');
    };
  }, [cloud]);

  useEffect(() => {
    if ((collectionId || discoverIntegrations) && workflow) {
      let mounted = true;
      let params;
      if (collectionId) {
        params = {
          route: 'discoverCollection',
          action: 'share',
          params: { collectionId },
        };
      } else {
        params = {
          route: 'discoverIntegrations',
          action: 'share',
          params: {},
        };
      }
      const handler = handleApiCall(commonApi.getCreativeCloudLink, params);
      (async () => {
        const response = await handler.run();
        if (mounted) {
          window.location.href = `${response?.deeplinkUrl}`;
        }
      })();
      return () => {
        mounted = false;
        handler.controller.abort();
      };
    }
  }, [collectionId, discoverIntegrations, workflow]);

  // Filter host apps by listingType if it exists else by cloud and update store with product list
  useEffect(() => {
    if (cloud && hostApps) {
      const productsList = [...hostApps]
        .filter(([_key, product]) => {
          const filterTypeMap = {
            solutions: 'sln',
            default: cloud,
          };
          const filterType = filterTypeMap[currentListingType] || filterTypeMap.default;
          return product?.showAsFilterFor?.includes(filterType);
        })
        .map(([key, product]) => {
          const { label, ...rest } = product;
          return { ...rest, key, name: label };
        });
      productsList.push({ key: 'All', name: 'All products' });
      useStore.setState({
        productsList,
      });
    }
  }, [cloud, currentListingType, hostApps]);

  // get cc most popular tags
  useEffect(() => {
    if (isCC) {
      let mounted = true;
      const handler = handleApiCall(commonApi.getCCMostPopularTags);
      (async () => {
        try {
          const tags = await handler.run();
          const priceFilter = [{ name: 'All' }, { name: 'Free' }, { name: 'Paid' }];
          if (mounted) {
            if (tags) {
              useStore.setState({
                filters: { price: priceFilter, tag: tags },
              });
            } else {
              useStore.setState({
                filters: { price: priceFilter },
              });
            }
          }
        } catch (err) {
          console.error(err);
        }
      })();
      return () => {
        mounted = false;
        handler.controller.abort();
      };
    }
  }, [isCC]);

  useEffect(() => {
    const newProduct = productsList?.find((p) => p.key === product);
    if (newProduct?.key !== selectedProduct?.key) {
      useStore.setState({ selectedProduct: newProduct });
    }
    // I am not adding selectedProduct to the dependencies because this is the only place where it is set
  }, [product, productsList]);

  const helmetProductName = selectedProduct?.key === 'All' ? `all ${cloudNames[cloud]}` : selectedProduct?.name ?? '';
  const getHelmetTitle = () => {
    const titles = {
      solutions: `${helmetProductName} solutions`,
      integrations: 'all app integrations',
      apps: `${helmetProductName} apps`,
    };

    return titles[currentListingType] || titles.apps;
  };

  const helmetTitle = getHelmetTitle();

  const banners = useMemo(
    () =>
      browseBanners
        ?.filter(
          (banner) =>
            banner?.rules?.products?.[cloud]
              ?.map((product) => product?.toLowerCase())
              ?.includes(selectedProduct?.key?.toLowerCase()) &&
            Date.now() > banner?.rules?.start &&
            (Date.now() < banner?.rules?.end || !banner?.rules?.end)
        )
        ?.reduce(
          (acc, { rules, ...banner }) =>
            rules?.position?.toLowerCase() === 'bottom'
              ? { ...acc, bottom: [...acc.bottom, banner] }
              : { ...acc, top: [...acc.top, banner] },
          { top: [], bottom: [] }
        ),
    [browseBanners, cloud, selectedProduct?.key]
  );

  // remove product=All from URL search params if the user selected 1 or more products
  useEffect(() => {
    if (typeof product === 'object' && product?.length > 1 && product?.includes('All')) {
      navigate(
        {
          search: qs.stringify(
            Object.assign({}, qs.parse(location.search), { product: product?.filter((p) => p !== 'All') })
          ),
        },
        { replace: true }
      );
    }
  }, [location.search, navigate, product]);

  return (
    <>
      <Helmet>
        <title>{`Browse ${helmetTitle} | Adobe Exchange`}</title>
        <meta name="robots" content="noodp, FOLLOW, INDEX" />
        <meta name="title" content={`Browse ${selectedProduct?.name} apps | Adobe Exchange`} />
        <meta property="og:title" content={`Browse ${selectedProduct?.name} apps | Adobe Exchange`} />
        <meta
          name="description"
          content="Experience Cloud: Explore the new Exchange Marketplace for Experience Cloud and Document Cloud."
        />
        <meta
          property="og:description"
          content="Experience Cloud: Explore the new Exchange Marketplace for Experience Cloud and Document Cloud."
        />
        <meta property="og:type" content="website" />
        <meta name="keywords" content="" />
      </Helmet>
      <BrowseContainer
        $isFlex={!isTabletSize}
        onClickCapture={(event) => {
          setTimeout(() => {
            const searchInfo = qs.parse(window.location.search);
            handleBrowseForLaunch(event, searchInfo);
          }, 0);
        }}
      >
        {!isTabletSize && (
          <View minWidth="276px" maxWidth="276px" elementType="aside">
            <SideBar />
          </View>
        )}
        <Flex direction="column" rowGap="size-500" maxWidth={isTabletSize ? '100%' : 'calc(100% - 292px)'} width="100%">
          {isCC && <ArmBanner />}
          <SelectedFilters filterKeys={filterKeys} />
          {shouldShowSearchBanner && !shouldHideFeaturedCarousel && isCC && (
            <CustomLinkBanner bgImgUrl={ccBanner} alignItems="center">
              <CustomLinkBanner.Title>Discover the power of plugins</CustomLinkBanner.Title>
              <SearchField isQuiet={false} width="100%" maxWidth="500px" hideLabel marginTop="size-250" />
            </CustomLinkBanner>
          )}
          {banners?.top?.length > 0 && !shouldHideTopBannersCarousel && (
            <View>
              <Carousel data-testid="banners-carousel">
                {banners?.top?.map((banner, index) => (
                  <CarouselItem key={banner?.title ?? index}>
                    <LinkBanner useVanillaLink {...banner} target="_blank" rel="noopener noreferrer" />
                  </CarouselItem>
                ))}
              </Carousel>
            </View>
          )}
          {!shouldHideFeaturedCarousel && <FeaturedSection />}
          {currentListingType !== 'integrations' && <SearchResults filterKeys={filterKeys} />}
          {currentListingType === 'integrations' && <AppIntegrations />}
          {banners?.bottom?.length > 0 && currentListingType !== 'solutions' && (
            <View>
              <Carousel data-testid="bottom-banners-carousel">
                {banners?.bottom?.map((banner, index) => (
                  <CarouselItem key={banner?.title ?? index}>
                    <LinkBanner
                      useVanillaLink
                      {...banner}
                      buttonHref={
                        banner?.buttonText === 'Search Creative Cloud'
                          ? `${banner?.buttonHref}${
                              qs.parse(location.search)?.q ? '?q=' + qs.parse(location.search)?.q : ''
                            }`
                          : banner?.buttonHref
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  </CarouselItem>
                ))}
              </Carousel>
            </View>
          )}
        </Flex>
      </BrowseContainer>
    </>
  );
};

export default Browse;
